/**
 * AdminOrbUniversesPage (/admin/orb-universes)
 *
 * Admin dashboard listing every Uplift universe provisioned via ORB's
 * create-universe call. Mobile-first — each universe is a collapsible
 * card showing community type, owner/member counts, theme, timezone,
 * Twilio number, and archive state.
 *
 * Pure read-only for v1. No destructive actions here — archive / member
 * lifecycle flows through ORB's webhooks, not an admin button.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Loader2, Users, Shield, Globe, MapPin, Phone,
  Clock, CheckCircle2, XCircle, Search, ExternalLink,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import * as perms from '../lib/permissions';

const fmtTs = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const Row = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-2 py-1 text-xs" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
    <span className="text-muted-foreground">{label}</span>
    <span className="ml-auto font-medium truncate">{value ?? '—'}</span>
  </div>
);

const UniverseCard = ({ u }) => {
  const [open, setOpen] = useState(false);
  const isArchived = !!u.is_archived;
  const signinUrl = `${window.location.origin}/u/${u.universe_id}/signin`;
  return (
    <div
      className={`rounded-xl border bg-white dark:bg-[#1a1a1a] shadow-sm overflow-hidden ${
        isArchived ? 'border-red-200/60 dark:border-red-900/40' : 'border-gray-200 dark:border-[#2a2a2a]'
      }`}
      data-testid={`orb-universe-${u.universe_id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-2 p-3 text-left hover:bg-gray-50 dark:hover:bg-[#0d0d0d] transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm truncate">{u.display_name || u.universe_id}</p>
          <p className="text-[11px] text-muted-foreground truncate">{u.community_type || '—'}</p>
          <div className="flex items-center gap-2 mt-1 text-[11px] flex-wrap">
            <span className={`px-1.5 py-0.5 rounded-full font-semibold ${
              isArchived
                ? 'bg-red-500/15 text-red-600'
                : 'bg-[#586D52]/15 text-[#586D52]'
            }`}>
              {isArchived ? 'Archived' : 'Active'}
            </span>
            <span className="text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" /> {u.active_members}/{u.total_members}
            </span>
            <span className="text-muted-foreground flex items-center gap-1">
              <Shield className="w-3 h-3" /> {u.supervisors} sup
            </span>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 dark:border-[#2a2a2a] p-3 space-y-0.5 bg-gray-50/50 dark:bg-[#0d0d0d]/50">
          <Row icon={Globe} label="ORB Community ID" value={u.orb_community_id} />
          <Row icon={Clock} label="Timezone" value={u.timezone} />
          <Row icon={Phone} label="Twilio number" value={u.phone_number} />
          <Row icon={MapPin} label="Theme" value={u.theme} />
          <Row icon={CheckCircle2} label="Created" value={fmtTs(u.created_at)} />
          {isArchived && (
            <>
              <Row icon={XCircle} label="Archived" value={fmtTs(u.archived_at)} />
              {u.archive_reason && (
                <Row icon={XCircle} label="Reason" value={u.archive_reason} />
              )}
            </>
          )}
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                // Browsers in insecure contexts / headless mode reject
                // clipboard writes with no permission prompt; swallow
                // the rejection so it doesn't trigger a runtime overlay.
                navigator.clipboard?.writeText(signinUrl).catch(() => {});
                toast.success('Signin URL copied');
              }}
              data-testid={`orb-universe-copy-${u.universe_id}`}
            >
              Copy signin URL
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs flex-shrink-0"
              onClick={(e) => { e.stopPropagation(); window.open(signinUrl, '_blank', 'noopener'); }}
              data-testid={`orb-universe-open-${u.universe_id}`}
            >
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminOrbUniversesPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // User wrote "ORB Universe" but the route handles many universes —
  // keep the title plural to match the data model.
  usePageHeader({ title: 'Universe' }, []);

  const isAdmin = user?.role === 'admin';
  const canAccess = perms.orbUniverses(user).view;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/orb-universes');
      setItems(r.data?.universes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load universes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  if (!canAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <p className="text-lg font-semibold">Admins only</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/')}>Back home</Button>
      </div>
    );
  }

  const query = q.trim().toLowerCase();
  const filtered = !query
    ? items
    : items.filter((u) =>
        (u.display_name || '').toLowerCase().includes(query) ||
        (u.orb_community_id || '').toLowerCase().includes(query) ||
        (u.community_type || '').toLowerCase().includes(query) ||
        (u.universe_id || '').toLowerCase().includes(query),
      );
  const active = filtered.filter((u) => !u.is_archived);
  const archived = filtered.filter((u) => u.is_archived);

  return (
    <div
      className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4"
      style={{
        top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))',
        bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="orb-universes-page"
    >
      <div className="max-w-3xl mx-auto">
        <p className="text-xs text-muted-foreground mb-4 max-w-prose">
          Every Uplift universe provisioned by ORB's create-universe webhook. Tap a card to expand.
          This list is read-only — member lifecycle and archive flow through ORB's webhooks.
        </p>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, type, or ORB community ID"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 text-sm"
            data-testid="orb-universes-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm font-semibold">
              {items.length === 0 ? 'No ORB universes yet' : 'No matches'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {items.length === 0
                ? "They'll appear here once ORB starts calling POST /universe."
                : 'Adjust your search to see more results.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {active.map((u) => (
              <UniverseCard key={u.universe_id} u={u} />
            ))}
            {archived.length > 0 && (
              <>
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mt-4 mb-1">
                  Archived ({archived.length})
                </p>
                {archived.map((u) => (
                  <UniverseCard key={u.universe_id} u={u} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
